import React, { Fragment, useState } from "react";
import { connect } from "react-redux";
import history from "../../history";
import * as style from "./styles.module.scss";

import { Button, Checkbox } from "../../common";
import VerificationCall from "../RightToWorkVerification";
import BranchDetails from "../BranchDetails";
import { pick } from "../../helpers";

const getImageName = (typeCode, documentType = "") => {
  const documentCaption = {
    passport: ["Your UK/Irish Passport"],
    shareCode: ["Completion document", "Enrolment letter"],
    expiredPassport: ["Your expired UK/Irish Passport"],
    visa: ["Front of your VISA", "Back of your VISA"],
    visaPassport: [
      "VISA page of your passport",
      "Picture and information page of your passport",
    ],
    birthCertificate: [
      "Your Birth/Adoption Certificate",
      "Your National Insurance Document",
    ],
    other: ["Your ID Document", "Additional ID image"],
  };

  switch (typeCode) {
    case "JPSE":
      return "Your selfie";
    case "Front":
      return documentCaption[documentType][0];
    case "Back":
      return documentCaption[documentType][1];
    case "JPPT":
      return "Your passport";
    case "JPPE":
      return "Your expired passport";
    case "JPVI":
      return "Visa page of your passport";
    case "JPVB":
      return "Picture and information page of your passport";
    case "JPID":
      return "Your ID";
    case "JPPB":
      return "Your ID (Additional image)";
    case "JPNI":
      return "Your Proof of NI";
    case "JBIR":
      return "Your birth certificate";
    case "NSHA":
      return "Your share code";
    case "JUNI":
      return "Enrolment letter";
    case "JCCC":
      return "Completion document";
    case "JUTD":
      return "Term dates";
    case "JIPC":
      return "Your in date UK or Irish passport/card";
    default:
      return "";
  }
};

const needsVisitToBranch = (documentType, rtwFiles) => {
  if (
    documentType === "passport" ||
    documentType === "shareCode" ||
    rtwFiles.find((f) => f.DisplayName.includes("Share"))
  ) {
    return false;
  }
  if (rtwFiles.find((f) => f.DisplayName.includes("RTW Passport"))) {
    return false;
  }
  return true;
};

const isRtwImage = ({ TypeCode }) =>
  !["NAPP", "WRTV", "WRTF", "YRTW", "YRTF"].includes(TypeCode);

const VerificationComplete = ({
  rtwFiles,
  returnBackLink,
  nextStepLink = "/profile",
}) => {
  return (
    <Fragment>
      <div className={`mb-4`}>
        <div className={"mb-4"}>
          Your Right to Work verification is now complete.
        </div>
        <div className={style.docsPreview}>
          {rtwFiles.filter(isRtwImage).map((file) => {
            return (
              <div className={style.docsPreview__container} key={file.FileRef}>
                {file.TypeCode !== "NSHA" ? (
                  <img
                    src={`data:image/jpeg;base64,${file.FileContent}`}
                    className={style.docsPreview__img}
                  />
                ) : (
                  <div className={style.docsPreview__sharecode}>
                    <p>
                      {Buffer.from(file.FileContent, "base64").toString(
                        "ascii"
                      )}
                    </p>
                  </div>
                )}
                <div className={style.docsPreview__caption}>
                  {/* Adds u200b (zero width space) so the word breaks on slash */}
                  {getImageName(file.TypeCode || file.DisplayName).replace(
                    /\//gi,
                    "/\u200b"
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div className={`mt-auto mb-lg-2 ${style.actions}`}>
        <Button
          classes="btn btn-outline-info text-dark mr-1"
          onClick={() => history.push(returnBackLink)}
        >
          Back
        </Button>
        <Button
          classes="btn btn-primary btn-block"
          onClick={() => history.push(nextStepLink)}
        >
          Next
        </Button>
      </div>
    </Fragment>
  );
};

const GoToBranch = ({
  rtwFiles,
  responsibleTeam,
  returnBackLink,
  nextStepLink = "/profile",
}) => {
  return (
    <Fragment>
      <div>
        <div className={"mb-2"}>
          <p>
            In line with government guidance, we now need to carry out a manual
            check of your original Right to Work documents. Please contact your
            local branch to arrange an appointment.
          </p>
        </div>
        <div className={`${style.docsPreview} mb-2`}>
          {rtwFiles.filter(isRtwImage).map((file) => {
            return (
              <div className={style.docsPreview__container} key={file.FileRef}>
                {file.TypeCode !== "NSHA" ? (
                  <img
                    src={`data:image/jpeg;base64,${file.FileContent}`}
                    className={style.docsPreview__img}
                  />
                ) : (
                  <div className={style.docsPreview__sharecode}>
                    <p>
                      {Buffer.from(file.FileContent, "base64").toString(
                        "ascii"
                      )}
                    </p>
                  </div>
                )}
                <div className={style.docsPreview__caption}>
                  {/* Adds u200b (zero width space) so the word breaks on slash */}
                  {getImageName(file.TypeCode || file.DisplayName).replace(
                    /\//gi,
                    "/\u200b"
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div>
          {responsibleTeam && (
            <Fragment>
              <p>
                <strong>Your local branch</strong>
              </p>
              <BranchDetails rtw={true} />
            </Fragment>
          )}
          {(!responsibleTeam || responsibleTeam === "HAPP") && (
            <p>
              <a
                href="/branch-finder"
                target="_blank"
                rel="noopener noreferrer"
              >
                Click here
              </a>{" "}
              to find your closest branch.
            </p>
          )}
        </div>
      </div>
      <div className={`mt-4 mb-lg-2 ${style.actions}`}>
        <Button
          classes="btn btn-outline-info text-dark mr-1"
          onClick={() => history.push(returnBackLink)}
        >
          Back
        </Button>
        <Button
          classes="btn btn-primary btn-block"
          onClick={() => {
            history.push(nextStepLink);
          }}
        >
          Next
        </Button>
      </div>
    </Fragment>
  );
};

const Verification = ({
  step,
  setStep,
  rtwFiles,
  documentType,
  responsibleTeam,
  rightToWorkVerified,
  rightToWorkVerifiedInFONT,
  returnBackLink,
  nextStepLink,
}) => {
  const [inQueue, joinQueue] = useState(false);

  const [checkboxesState, setCheckboxesState] = useState({
    camAndMic: false,
    saveImage: false,
  });

  if (rightToWorkVerified || rightToWorkVerifiedInFONT) {
    return (
      <VerificationComplete
        rtwFiles={rtwFiles}
        returnBackLink={returnBackLink}
        nextStepLink={nextStepLink}
      />
    );
  }

  if (needsVisitToBranch(documentType, rtwFiles)) {
    return (
      <GoToBranch
        rtwFiles={rtwFiles}
        responsibleTeam={responsibleTeam}
        returnBackLink={returnBackLink}
        nextStepLink={nextStepLink}
      />
    );
  }

  return (
    <Fragment>
      {inQueue && (
        <VerificationCall leaveQueue={joinQueue} nextStepLink={nextStepLink} />
      )}
      {!inQueue && (
        <Fragment>
          <div>
            <div className="mb-1">
              <div className={"mb-1"}>
                <p className={"mb-1"}>
                  By law, we must check your right to work documents before you
                  can start work. We can check your documents using our HRGO
                  online video call. After you join the queue, you may need to
                  wait a few minutes for an HRGO video call operator to become
                  available.
                </p>
                <p className={"mb-1"}>
                  We will check the share code you have uploaded on the
                  government website while speaking with you on the call.
                </p>
                <p className={"mb-1 font-weight-bold"}>
                  Please confirm the following before joining the video call
                  queue:
                </p>
              </div>
              <Checkbox
                label="The device I am using has a camera and a microphone"
                name="camAndMic"
                onClick={() =>
                  setCheckboxesState({
                    ...checkboxesState,
                    camAndMic: !checkboxesState.camAndMic,
                  })
                }
                isChecked={checkboxesState.camAndMic}
              />
              <Checkbox
                label="I consent to HRGO viewing my Right to Work documentation that
               is produced from the share code check and saving an image of the video call taking place"
                name="saveImage"
                onClick={() =>
                  setCheckboxesState({
                    ...checkboxesState,
                    saveImage: !checkboxesState.saveImage,
                  })
                }
                isChecked={checkboxesState.saveImage}
              />
              <p style={{ whiteSpace: "pre-line" }}>
                You can find more details on how your information is used in the{" "}
                <a
                  href="https://www.hrgo.co.uk/legal-policies/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={style.link}
                >
                  HRGO privacy policy
                </a>
                .
              </p>
            </div>
            <div className={style.docsPreview}>
              {rtwFiles.filter(isRtwImage).map((file) => {
                return (
                  <div
                    className={style.docsPreview__container}
                    key={file.FileRef}
                  >
                    {file.TypeCode !== "NSHA" ? (
                      <img
                        src={`data:image/jpeg;base64,${file.FileContent}`}
                        className={style.docsPreview__img}
                      />
                    ) : (
                      <div className={style.docsPreview__sharecode}>
                        <p>
                          {Buffer.from(file.FileContent, "base64").toString(
                            "ascii"
                          )}
                        </p>
                      </div>
                    )}

                    <div className={style.docsPreview__caption}>
                      {/* Adds u200b (zero width space) so the word breaks on slash */}
                      {getImageName(
                        file.TypeCode || file.DisplayName,
                        documentType
                      ).replace(/\//gi, "/\u200b")}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className={`mt-4 mb-lg-2 ${style.actions}`}>
            <Button
              classes="btn btn-outline-info text-dark mr-1"
              onClick={() => setStep(step - 1)}
            >
              Back
            </Button>

            <Button
              classes="btn btn-primary btn-block"
              onClick={() => joinQueue(true)}
              disabled={
                !(checkboxesState.camAndMic && checkboxesState.saveImage)
              }
            >
              Join call queue
            </Button>
          </div>
        </Fragment>
      )}
    </Fragment>
  );
};

const mapStateToProps = (state) => {
  const appData = state.core.app || {};
  const rtwVerification = pick(
    appData,
    ["rightToWorkVerified", "rightToWorkVerifiedInFONT"],
    null
  );
  return { appData, ...rtwVerification };
};

const mapDispatchToProps = {};

const withRedux = connect(mapStateToProps, mapDispatchToProps);

export default withRedux(Verification);
